import React, { useEffect, useState } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import useDraggable from '../hooks/useDraggable';
import { TwoFactorSetup, BackupCodesView } from '../modules/security';
import { apiJson, QrImage, twoFactorStrings, TFA_BUTTONS } from './twoFactorShared';

// Profil > İki Adımlı Doğrulama (madde 12) — her kullanıcı kendi 2FA'sını kurar / kapatır /
// yeni yedek kod üretir. Kurulum ve değişiklikler şifre onayı ister (modül 1.2.0). Bileşenler
// ortak güvenlik modülünden; backend /api/auth/2fa (backend/src/twoFactor.js).
export default function TwoFactorModal({ token, onClose }) {
  const { t, lang } = useTranslation();
  const { offset, onMouseDown } = useDraggable();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);

  const load = () => apiJson('GET', '/api/auth/2fa/status', undefined, token).then(setStatus).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const withPassword = async (url, after) => {
    setBusy(true); setError(''); setInfo('');
    try { after(await apiJson('POST', url, { password }, token)); setPassword(''); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const methodLabel = status?.method === 'SMS' ? t('tfa.methodSms', { phone: status.phoneHint || '' }) : t('tfa.methodTotp');

  return (
    <div className="settings-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="settings-modal ts-tfa-modal" style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}>
        <div className="settings-header" onMouseDown={onMouseDown}>
          <span className="settings-title">{t('tfa.title')}</span>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>
        <div className="settings-body">
          {error && <div className="auth-alert auth-alert-error">{error}</div>}
          {info && <div className="auth-alert auth-alert-success">{info}</div>}
          {!status && !error && <p className="settings-hint">…</p>}

          {codes && (
            <BackupCodesView codes={codes} onDone={() => { setCodes(null); load(); }} strings={twoFactorStrings(lang)} buttons={TFA_BUTTONS} />
          )}

          {status && !codes && status.enabled && (
            <div className="ts-tfa-status">
              <p><strong>{t('tfa.enabled')}</strong> — {methodLabel}</p>
              <p className="settings-hint">{t('tfa.backupRemaining', { n: status.backupCodesRemaining })}</p>
              <label className="settings-label">{t('tfa.password')}</label>
              <input className="settings-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <div className="ts-tfa-actions">
                <button className="btn" disabled={busy || !password} onClick={() => withPassword('/api/auth/2fa/backup-codes', (d) => setCodes(d.backupCodes))}>
                  {t('tfa.newBackupCodes')}
                </button>
                {!status.required && (
                  <button className="btn btn-stop" disabled={busy || !password} onClick={() => withPassword('/api/auth/2fa/disable', (d) => { setInfo(d.message); load(); })}>
                    {t('tfa.disable')}
                  </button>
                )}
              </div>
              {status.required && <p className="settings-hint">{t('tfa.requiredNote')}</p>}
            </div>
          )}

          {status && !codes && !status.enabled && status.canEnable && (
            <TwoFactorSetup
              requirePassword
              smsAvailable={status.smsAvailable}
              start={(method, phone, pw) => apiJson('POST', '/api/auth/2fa/setup', { method, phone, password: pw }, token)}
              enable={async (code) => (await apiJson('POST', '/api/auth/2fa/enable', { code }, token)).backupCodes}
              onDone={() => { setInfo(t('tfa.enabledNow')); load(); }}
              onError={(err) => setError(err?.message || String(err))}
              renderQr={(url) => <QrImage text={url} />}
              renderPhoneInput={(value, onChange) => (
                <input className="settings-input" type="tel" placeholder="5XX XXX XX XX" value={value} onChange={(e) => onChange(e.target.value)} />
              )}
              strings={twoFactorStrings(lang)}
              buttons={TFA_BUTTONS}
            />
          )}

          {status && !codes && !status.enabled && !status.canEnable && (
            <p className="settings-note">{status.exempt ? t('tfa.googleExempt') : t('tfa.disabledByAdmin')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
